import { FileTypeEnum } from 'api/documents';
import { ExclamationPointIcon } from 'components/icons/customIcons';
import { CheckIcon, ChevronDownIcon, ChevronUpIcon, DocumentIcon } from 'components/icons/Icons';
import { FC, useCallback, useState, ReactNode } from 'react';
import { GenericDocumentUploader } from './GenericDocumentUploader';
import { GenericMultiDocumentUploader, UploadedFile } from './GenericMultiDocumentUploader';

export interface DocumentItem {
  fileType: FileTypeEnum;
  name: string;
  /** Premier fichier fourni (le seul pour un document mono-fichier) */
  storedFileId?: string;
  /** Document constitué de plusieurs fichiers : la zone d'ajout reste disponible après le premier */
  allowMultiple?: boolean;
  /** Tous les fichiers fournis (documents multi-fichiers) */
  files?: UploadedFile[];
}

export interface DocumentSection {
  id: string;
  title: string;
  icon?: React.ReactNode;
  documents: DocumentItem[];
  isEligible?: boolean;
  isCompleted?: boolean;
  /** Libellé affiché quand la section est complète — remplace completedSectionLabel (ex. locataire en place) */
  completedLabel?: string;
  /** Message affiché quand la section n'a aucun document — remplace emptyMessage global */
  emptyMessage?: string;
}

interface GenericDocumentsManagerProps {
  disabledSectionLabel?: string
  completedSectionLabel?: string
  sections: DocumentSection[];
  onUpload: (sectionId: string, file: File, fileType: FileTypeEnum) => Promise<void>;
  onRemove: (sectionId: string, storedFileId: string) => Promise<void>;
  title?: string;
  emptyMessage?: string;
  renderUploadedActions?: (
    storedFileId: string,
    onRemove: () => void,
    sectionId: string,
    document: DocumentItem
  ) => ReactNode;
  renderCustomDocumentItem?: (
    document: DocumentItem,
    sectionId: string
  ) => ReactNode | null;
  acceptedFormats?: string;
  disabled?: boolean;
}

export const GenericDocumentsManager: FC<GenericDocumentsManagerProps> = ({
  sections,
  onUpload,
  onRemove,
  title,
  completedSectionLabel = 'Dossier complet',
  disabledSectionLabel = 'Section non disponible',
  emptyMessage = 'Aucun document requis',
  renderUploadedActions,
  renderCustomDocumentItem,
  acceptedFormats,
  disabled = false
}) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

  const toggleSectionExpansion = useCallback((sectionId: string, isEligible: boolean = true) => {
    if (!isEligible) return;
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  }, []);

  const handleUpload = useCallback(async (
    sectionId: string,
    file: File,
    fileType: FileTypeEnum
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      await onUpload(sectionId, file, fileType);
      return { success: true };
    } catch (error) {
      console.error(error);
      return { success: false, error: 'Une erreur est survenue' };
    }
  }, [onUpload]);

  const handleRemove = useCallback(async (sectionId: string, storedFileId: string) => {
    try {
      if (onRemove)
        await onRemove(sectionId, storedFileId);
    } catch (error) {
      console.error(error);
    }
  }, [onRemove]);

  const renderDocumentItem = useCallback((
    document: DocumentItem,
    sectionId: string
  ) => {
    if (renderCustomDocumentItem) {
      const custom = renderCustomDocumentItem(document, sectionId);
      if (custom) return custom;
    }
    if (document.allowMultiple) {
      return (
        <GenericMultiDocumentUploader
          key={`${sectionId}-${document.fileType}`}
          documentName={document.name}
          fileType={document.fileType}
          files={document.files ?? []}
          onUpload={(file, fileType) => handleUpload(sectionId, file, fileType)}
          onRemove={(storedFileId) => handleRemove(sectionId, storedFileId)}
          acceptedFormats={acceptedFormats}
          disabled={disabled}
        />
      );
    }
    return (
      <GenericDocumentUploader
        key={`${sectionId}-${document.fileType}`}
        documentName={document.name}
        fileType={document.fileType}
        storedFileId={document.storedFileId}
        onUpload={(file, fileType) => handleUpload(sectionId, file, fileType)}
        onRemove={(storedFileId) => handleRemove(sectionId, storedFileId)}
        renderUploadedActions={
          renderUploadedActions
            ? (storedFileId, onRemoveCallback: any) =>
              renderUploadedActions(storedFileId, onRemoveCallback, sectionId, document)
            : undefined
        }
        acceptedFormats={acceptedFormats}
        disabled={disabled}
      />
    );
  }, [handleUpload, handleRemove, renderUploadedActions, renderCustomDocumentItem, acceptedFormats, disabled]);

  return (
    <div className="space-y-4">
      {title && <h3 className="text-lg font-semibold">{title}</h3>}
      <div className="space-y-3">
        {sections.map((section) => {
          const isEligible = section.isEligible !== false;
          const isExpanded = expandedSections.has(section.id);
          const uploadedDocsCount = section.documents.filter(doc => doc.storedFileId).length;
          const totalDocsCount = section.documents.length;

          return (
            <div
              key={section.id}
              className={`border rounded-2xl shadow overflow-hidden ${isEligible ? 'bg-white' : 'bg-gray-100 opacity-60'
                }`}
            >
              {/* En-tête de la section */}
              <div
                className={`flex items-center justify-between p-4 transition-colors ${isEligible ? 'cursor-pointer hover:bg-gray-50' : 'cursor-not-allowed'
                  }`}
                onClick={() => toggleSectionExpansion(section.id, isEligible)}
              >
                <div className="flex items-center gap-4 flex-1">
                  <div className="flex items-center gap-3">
                    {section.icon}
                    <span className="font-medium">{section.title}</span>
                  </div>

                  {isEligible ? (
                    <div className={`flex items-center gap-2 text-sm ${section.isCompleted ? 'text-green-600' : 'text-orange-600'
                      }`}>
                      {section.isCompleted ? (
                        <div className="p-1 rounded-full bg-green-500 text-white">
                          <CheckIcon className="w-3 h-3" />
                        </div>
                      ) : (
                        <div className="p-1 rounded-full bg-orange-500 text-white">
                          <ExclamationPointIcon className="w-3 h-3" />
                        </div>
                      )}
                      <span>
                        {section.isCompleted
                          ? (section.completedLabel ?? completedSectionLabel)
                          : `${uploadedDocsCount}/${totalDocsCount} documents`
                        }
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <div className="p-1 rounded-full bg-gray-400 text-white">
                        <ExclamationPointIcon className="w-3 h-3" />
                      </div>
                      <span>{disabledSectionLabel}</span>
                    </div>
                  )}
                </div>

                {isEligible && (
                  <div className="flex items-center gap-3">
                    {totalDocsCount > 0 && (
                      <div className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                        {uploadedDocsCount}/{totalDocsCount}
                      </div>
                    )}
                    {isExpanded ? (
                      <ChevronUpIcon className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDownIcon className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                )}
              </div>

              {/* Section des documents (accordéon) */}
              {isExpanded && isEligible && (
                <div className="border-t bg-gray-50 p-4">
                  {section.documents.length > 0 ? (
                    <div className="space-y-3">
                      {section.documents.map((document) =>
                        renderDocumentItem(document, section.id)
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-6 text-gray-500">
                      <DocumentIcon className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                      <p className="text-sm">{section.emptyMessage ?? emptyMessage}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
